import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ServiceComparison = ({ services }) => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  const toggleService = (serviceId) => {
    setSelectedServices(prev => {
      if (prev?.includes(serviceId)) {
        return prev?.filter(id => id !== serviceId);
      }
      if (prev?.length < 3) {
        return [...prev, serviceId];
      }
      return prev;
    });
  };

  const comparisonData = selectedServices?.map(id =>
    services?.find(s => s?.id === id)
  )?.filter(Boolean);

  return (
    <div className="bg-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-4 md:p-6 shadow-2xl mb-6 md:mb-8">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-headline)' }}>
            Comparer les services
          </h2>
          <p className="text-sm text-slate-400">
            Sélectionnez jusqu'à 3 services pour comparer leurs caractéristiques
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">
            {selectedServices?.length}/3 sélectionnés
          </span>
          {selectedServices?.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={() => setSelectedServices([])}
            >
              Effacer
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {services?.slice(0, 8)?.map((service) => (
          <label
            key={service?.id}
            className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
              selectedServices?.includes(service?.id)
                ? 'border-blue-500 bg-blue-500/5' :'border-white/10 hover:border-blue-500/50'
            }`}
          >
            <Checkbox
              checked={selectedServices?.includes(service?.id)}
              onChange={() => toggleService(service?.id)}
              disabled={!selectedServices?.includes(service?.id) && selectedServices?.length >= 3}
            />
            <span className="text-xs md:text-sm font-medium text-white line-clamp-1">
              {service?.title}
            </span>
          </label>
        ))}
      </div>
      {selectedServices?.length >= 2 && (
        <Button
          variant="default"
          fullWidth
          iconName={showComparison ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          onClick={() => setShowComparison(!showComparison)}
        >
          {showComparison ? 'Masquer' : 'Afficher'} la comparaison
        </Button>
      )}
      <AnimatePresence>
        {showComparison && comparisonData?.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 overflow-x-auto"
          >
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-sm font-semibold text-white">Caractéristique</th>
                  {comparisonData?.map((service) => (
                    <th key={service?.id} className="text-left p-3">
                      <div className="flex items-center gap-2">
                        <Icon name={service?.icon} size={20} className="text-blue-400" />
                        <span className="text-sm font-semibold text-white">{service?.title}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10">
                  <td className="p-3 text-sm font-medium text-slate-400">Catégorie</td>
                  {comparisonData?.map((service) => (
                    <td key={service?.id} className="p-3 text-sm text-white">{service?.category}</td>
                  ))}
                </tr>
                <tr className="border-b border-white/10">
                  <td className="p-3 text-sm font-medium text-slate-400">Délai de livraison</td>
                  {comparisonData?.map((service) => (
                    <td key={service?.id} className="p-3 text-sm text-white">{service?.deliveryTime}</td>
                  ))}
                </tr>
                <tr className="border-b border-white/10">
                  <td className="p-3 text-sm font-medium text-slate-400">Prix de départ</td>
                  {comparisonData?.map((service) => (
                    <td key={service?.id} className="p-3 text-sm font-semibold text-blue-400">{service?.startingPrice}</td>
                  ))}
                </tr>
                <tr className="border-b border-white/10">
                  <td className="p-3 text-sm font-medium text-slate-400">Caractéristiques</td>
                  {comparisonData?.map((service) => (
                    <td key={service?.id} className="p-3">
                      <ul className="space-y-1">
                        {service?.features?.slice(0, 3)?.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-1 text-xs text-slate-300">
                            <Icon name="Check" size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-1">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceComparison;
